import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('WindowMessenger', 'window-communication');

// Message types for cross-window communication
export type MessageType = 
  | 'AUTH_UPDATE' 
  | 'USER_PROFILE' 
  | 'THEME_CHANGE' 
  | 'DATA_SYNC'
  | 'REQUEST_AUTH'
  | 'ALERT_NOTIFICATION'
  | 'WINDOW_READY'
  | 'WINDOW_CLOSING';

export interface WindowMessage {
  source: 'lendwise';
  type: MessageType;
  payload: any;
  timestamp: number;
  windowId?: string;
}

export interface AuthPayload {
  token: string | null;
  refreshToken?: string;
  user?: any;
  expiresAt?: number;
}

type MessageHandler = (payload: any, origin: string) => void;

export class WindowMessenger {
  private static instance: WindowMessenger;
  private windows: Map<string, Window> = new Map();
  private messageHandlers: Map<MessageType, MessageHandler[]> = new Map();
  private windowId: string;
  private allowedOrigins: Set<string> = new Set();

  private constructor() {
    this.windowId = `lw-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Set up allowed origins from environment
    const mainAppUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const dashboardUrl = import.meta.env.VITE_MARKET_DASHBOARD_URL;
    
    this.allowedOrigins.add(mainAppUrl);
    if (dashboardUrl) {
      try {
        // Handle both absolute and relative URLs
        const url = dashboardUrl.startsWith('http') 
          ? dashboardUrl 
          : new URL(dashboardUrl, window.location.origin).href;
        this.allowedOrigins.add(new URL(url).origin);
      } catch (error) {
        log.warn('Invalid dashboard URL, using current origin', { dashboardUrl, error });
        this.allowedOrigins.add(window.location.origin);
      }
    }
    
    // Set up global message listener
    window.addEventListener('message', this.handleMessage.bind(this));
    
    // Clean up on window close
    window.addEventListener('beforeunload', this.cleanup.bind(this));
    
    log.debug('WindowMessenger initialized', { windowId: this.windowId });
  }

  static getInstance(): WindowMessenger {
    if (!this.instance) {
      this.instance = new WindowMessenger();
    }
    return this.instance;
  }

  // Register a window for communication
  registerWindow(name: string, windowRef: Window): void {
    this.windows.set(name, windowRef);
    log.debug('Window registered', { name, windowId: this.windowId });
    
    // Monitor if window closes
    const checkInterval = setInterval(() => {
      if (windowRef.closed) {
        this.windows.delete(name);
        clearInterval(checkInterval);
        log.info('Window closed and unregistered', { name });
      }
    }, 1000);
  }

  // Send message to specific window
  sendMessage(targetWindow: string, message: Omit<WindowMessage, 'source' | 'timestamp' | 'windowId'>): void {
    const window = this.windows.get(targetWindow);
    
    if (!window || window.closed) {
      log.warn('Target window not available', { targetWindow });
      return;
    }

    const fullMessage: WindowMessage = {
      source: 'lendwise',
      ...message,
      timestamp: Date.now(),
      windowId: this.windowId
    };

    try {
      const targetOrigin = targetWindow === 'market-dashboard' 
        ? import.meta.env.VITE_MARKET_DASHBOARD_URL 
        : import.meta.env.VITE_APP_URL || window.location.origin;
      
      window.postMessage(fullMessage, targetOrigin);
      log.debug('Message sent', { targetWindow, type: message.type });
    } catch (error) {
      log.error('Failed to send message', { targetWindow, error });
    }
  }

  // Broadcast message to all registered windows
  broadcast(message: Omit<WindowMessage, 'source' | 'timestamp' | 'windowId'>): void {
    this.windows.forEach((_, name) => {
      this.sendMessage(name, message);
    });
  }

  // Handle incoming messages
  private handleMessage(event: MessageEvent): void {
    // Validate origin
    if (!this.allowedOrigins.has(event.origin)) {
      log.warn('Message from unauthorized origin', { origin: event.origin });
      return;
    }

    // Validate message structure
    if (!event.data || event.data.source !== 'lendwise') {
      return;
    }

    const message = event.data as WindowMessage;
    log.debug('Message received', { type: message.type, origin: event.origin });

    // Execute handlers
    const handlers = this.messageHandlers.get(message.type) || [];
    handlers.forEach(handler => {
      try {
        handler(message.payload, event.origin);
      } catch (error) {
        log.error('Message handler error', { type: message.type, error });
      }
    });
  }

  // Register message handler
  on(messageType: MessageType, handler: MessageHandler): () => void {
    const handlers = this.messageHandlers.get(messageType) || [];
    handlers.push(handler);
    this.messageHandlers.set(messageType, handlers);
    
    // Return unsubscribe function
    return () => {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    };
  }

  // Remove all handlers for a message type
  off(messageType: MessageType): void {
    this.messageHandlers.delete(messageType);
  }

  // Clean up resources
  private cleanup(): void {
    // Notify other windows this window is closing
    this.broadcast({
      type: 'WINDOW_CLOSING',
      payload: { windowId: this.windowId }
    });
    
    // Clear all windows and handlers
    this.windows.clear();
    this.messageHandlers.clear();
    
    log.info('WindowMessenger cleaned up');
  }

  // Get window ID
  getWindowId(): string {
    return this.windowId;
  }

  // Check if a window is registered and open
  isWindowOpen(name: string): boolean {
    const window = this.windows.get(name);
    return !!window && !window.closed;
  }

  // Add origin to allowed list (for dynamic origins)
  addAllowedOrigin(origin: string): void {
    this.allowedOrigins.add(origin);
    log.debug('Added allowed origin', { origin });
  }
}

// Singleton export
export const windowMessenger = WindowMessenger.getInstance();